import { type FC, type ReactNode, useLayoutEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAtom } from "jotai";
import { themeAtom } from "@/state/theme";
import { COMPANY_CONFIG } from "@/constants/companyData";
import { SUPPORTED_THEMES } from "@/constants/themes";
import type { Theme } from "@/types/Theme";

const THEME_SESSION_KEY = "calculator.theme";

export const ThemeProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const [theme, setTheme] = useAtom(themeAtom);

  const isTheme = (value: string | null): value is Theme =>
    SUPPORTED_THEMES.includes(value as Theme);

  useLayoutEffect(() => {
    const themeParam = searchParams.get("theme");

    if (isTheme(themeParam)) {
      setTheme(themeParam);
      window.sessionStorage.setItem(THEME_SESSION_KEY, themeParam);
      return;
    }

    const storedTheme = window.sessionStorage.getItem(THEME_SESSION_KEY);
    const resolvedTheme: Theme = isTheme(storedTheme) ? storedTheme : "default";

    setTheme(resolvedTheme);
  }, [searchParams, setTheme]);

  useLayoutEffect(() => {
    const html = document.documentElement;

    html.classList.remove("vtac");
    if (theme !== "default") {
      html.classList.add(theme);
    }

    document.title = COMPANY_CONFIG[theme].title;

    const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (favicon) {
      favicon.href = COMPANY_CONFIG[theme].favicon;
    } else {
      const newFavicon = document.createElement("link");
      newFavicon.rel = "icon";
      newFavicon.href = COMPANY_CONFIG[theme].favicon;
      document.head.appendChild(newFavicon);
    }
  }, [theme]);

  return <>{children}</>;
};
